import { Component, ReactNode } from 'react'
import { clearAllAppData } from '../utils/cacheUtils'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: any
}

export class ErrorBoundary extends Component<Props, State> {
  private errorCount = 0
  private maxErrors = 3

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: any) {
    console.error('🚨 Error Boundary caught error:', error, errorInfo)

    this.errorCount++
    this.setState({ error, errorInfo })

    // If we hit too many errors, clear cache and reload
    if (this.errorCount >= this.maxErrors) {
      console.log('🧹 Too many errors detected, clearing cache and reloading...')
      clearAllAppData()

      // Add a small delay before reload to ensure cache clearing completes
      setTimeout(() => {
        window.location.reload()
      }, 1000)
    }
  }

  private handleClearCacheAndReload = () => {
    console.log('🔄 User requested cache clear and reload')
    clearAllAppData()
    window.location.reload()
  }

  private handleForceRefresh = () => {
    console.log('🚀 User requested force refresh with cache busting')
    clearAllAppData()

    // Add cache busting parameter and force refresh
    const url = new URL(window.location.href)
    url.searchParams.set('_cacheBust', Date.now().toString())
    url.searchParams.set('_forceRefresh', 'true')
    window.location.replace(url.toString())
  }

  private handleSimpleReload = () => {
    window.location.reload()
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-muted px-4">
          <div className="max-w-md w-full bg-surface rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-strong">
                  Application Error
                </h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted">
                Something went wrong. This might be due to corrupted cache data.
              </p>

              {this.errorCount >= 2 && (
                <div
                  className="mt-2 p-3 border rounded"
                  style={{
                    borderColor: 'color-mix(in srgb, var(--color-accent) 35%, transparent)',
                    backgroundColor: 'color-mix(in srgb, var(--color-accent) 10%, var(--color-surface))'
                  }}
                >
                  <p className="text-sm" style={{ color: 'color-mix(in srgb, var(--color-accent) 70%, var(--color-text))' }}>
                    <strong>Repeated errors detected.</strong> This usually indicates corrupted browser data.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleSimpleReload}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors"
              >
                Try Again
              </button>

              <button
                onClick={this.handleClearCacheAndReload}
                className="w-full bg-[var(--color-accent)] hover:brightness-110 text-white font-medium py-2 px-4 rounded transition-colors"
              >
                Clear Cache & Reload
              </button>

              <button
                onClick={this.handleForceRefresh}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded transition-colors"
              >
                Force Hard Refresh (CloudFlare Fix)
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4">
                <summary className="text-sm text-subtle cursor-pointer">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs bg-surface-muted p-2 rounded overflow-auto max-h-32">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
